import { DateUtils } from '@web_opxp/features';
import { DBSchema, IDBPDatabase } from 'idb';
import reduce from 'lodash/reduce';
import getCacheDB, { registerUpgradeFunction } from '../base/cacheDB';
import { CYCLES_CLIENT_DB_EXPIRATION_TIMEOUT_SEC } from './constants';
import {
  CyclesDBError,
  IClientDBSync,
  ICycleClientStatus,
  ISessionClientStatus,
  ITrainingClientStatus,
  IWorkoutClientStatus,
} from './types';

type TTrainingStoreSchema = {
  key: number;
  value: ITrainingClientStatus;
};

type TCyclesStoreSchema = {
  key: number;
  value: ICycleClientStatus;
};

type TSessionsStoreSchema = {
  key: string;
  value: ISessionClientStatus;
};

type TWorkoutsStoreSchema = {
  key: string;
  value: IWorkoutClientStatus;
};

interface ICyclesDBSchema extends DBSchema {
  training: TTrainingStoreSchema;
  cycles: TCyclesStoreSchema;
  sessions: TSessionsStoreSchema;
  workouts: TWorkoutsStoreSchema;
}

export const clientDBValueNeedsSync = (value: IClientDBSync) => value.syncTimestamp === 0;

export function clientDBValueExpired(value?: IClientDBSync) {
  if (!value) {
    // undefined means no value = expired
    return true;
  }
  if (!value.syncTimestamp) {
    // syncTimestamp=0 means value was not saved to backend
    return false;
  }
  const current = DateUtils.getUnixTimestamp();
  return current - value.syncTimestamp >= CYCLES_CLIENT_DB_EXPIRATION_TIMEOUT_SEC;
}

export function clientDBArrayExpired(values?: IClientDBSync[]) {
  if (!values) {
    return true;
  }
  return reduce(
    values,
    (exp, value) => {
      return exp || clientDBValueExpired(value);
    },
    false
  );
}

function padNumber(num: number, length = 3) {
  const str = `${num}`;
  return str.padStart(length, '0');
}

registerUpgradeFunction<ICyclesDBSchema>((db) => {
  if (!db.objectStoreNames.contains('training')) {
    db.createObjectStore('training');
  }
  if (!db.objectStoreNames.contains('cycles')) {
    db.createObjectStore('cycles', { keyPath: 'cycleId' });
  }
  if (!db.objectStoreNames.contains('sessions')) {
    db.createObjectStore('sessions');
  }
  if (!db.objectStoreNames.contains('workouts')) {
    db.createObjectStore('workouts');
  }
});

function initCyclesDB() {
  let userId: number;
  let dbPromise: Promise<IDBPDatabase<ICyclesDBSchema>> | undefined;
  const init = (uId: number) => {
    userId = uId;
    dbPromise = getCacheDB<ICyclesDBSchema>(uId);
  };
  // training stats store functions
  async function getTrainingStats() {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).get('training', userId);
  }
  async function saveTrainingStats(stats: ITrainingClientStatus) {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).put('training', stats, userId);
  }
  async function clearTrainingStats() {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).clear('training');
  }

  // cycles store functions
  async function getCycle(cycleId: number) {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).get('cycles', cycleId);
  }
  async function saveCycle(cycleStatus: ICycleClientStatus) {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).put('cycles', cycleStatus);
  }
  async function clearCycles() {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).clear('cycles');
  }

  // sessions store functions
  const buildSessionKeyPrefix = (cycleId: number) => `${padNumber(cycleId)}`;
  const buildSessionKey = (cycleId: number, sessionId: number) => `${padNumber(cycleId)}-${padNumber(sessionId)}`;
  async function getSession(cycleId: number, sessionId: number) {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).get('sessions', buildSessionKey(cycleId, sessionId));
  }
  async function getAllSessionsForCycle(cycleId: number) {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    const startKey = buildSessionKeyPrefix(cycleId);
    const endKey = buildSessionKeyPrefix(cycleId + 1);
    const keyRange = IDBKeyRange.bound(startKey, endKey, true, true);
    return (await dbPromise).getAll('sessions', keyRange);
  }
  async function saveSession(session: ISessionClientStatus) {
    const { cycleId, sessionId } = session;
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).put('sessions', session, buildSessionKey(cycleId, sessionId));
  }
  async function clearSessions() {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).clear('sessions');
  }

  // workouts store functions
  const buildWorkoutKeyPrefix = (cycleId: number, sessionId: number) => `${padNumber(cycleId)}-${padNumber(sessionId)}`;
  const buildWorkoutKey = (cycleId: number, sessionId: number, workoutIndex: number) =>
    `${buildWorkoutKeyPrefix(cycleId, sessionId)}-${padNumber(workoutIndex)}`;
  async function getWorkouts(cycleId: number, sessionId: number) {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    const startKey = buildWorkoutKeyPrefix(cycleId, sessionId);
    const endKey = buildWorkoutKeyPrefix(cycleId, sessionId + 1);
    const keyRange = IDBKeyRange.bound(startKey, endKey, true, true);
    return (await dbPromise).getAll('workouts', keyRange);
  }
  async function saveWorkout(workout: IWorkoutClientStatus) {
    const { cycleId, sessionId, index } = workout;
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).put('workouts', workout, buildWorkoutKey(cycleId, sessionId, index));
  }
  async function saveWorkouts(workouts: IWorkoutClientStatus[]) {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    const transaction = (await dbPromise).transaction('workouts', 'readwrite');
    workouts.forEach(async (workout) => {
      const { cycleId, sessionId, index } = workout;
      const dbKey = buildWorkoutKey(cycleId, sessionId, index);
      await transaction.store.put(workout, dbKey);
    });
    return transaction.done;
  }
  async function clearWorkouts() {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    return (await dbPromise).clear('workouts');
  }
  async function clear() {
    if (!dbPromise) {
      throw new CyclesDBError('DB not initialized!');
    }
    await Promise.all([clearTrainingStats(), clearCycles(), clearSessions(), clearWorkouts()]);
    return (await dbPromise).close();
  }

  return {
    init,
    getTrainingStats,
    saveTrainingStats,
    clearTrainingStats,
    getCycle,
    saveCycle,
    clearCycles,
    getSession,
    getAllSessionsForCycle,
    saveSession,
    clearSessions,
    getWorkouts,
    saveWorkout,
    saveWorkouts,
    clearWorkouts,
    clear,
  };
}

export type TCyclesDB = ReturnType<typeof initCyclesDB>;

const CyclesDB = initCyclesDB();
export default CyclesDB;
